import classNames from 'classnames';
import {WithRouterProps} from 'react-router';
import React, {useEffect} from 'react';
import {useShallow} from 'zustand/react/shallow';

import appStore from 'client/AppStore';
import ClassroomInvitations from './ClassroomInvitations';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {
  HardCapStudentErrorModal,
  InvalidEmailModal
} from 'client/Classrooms/StudentDashboard/Modals';
import JoinClassroomModal from 'client/Classrooms/JoinClassroom/JoinClassroomModal';
import {LoadingSpinner} from '@albert-io/atomic';
import StudentHomepageHeader from './StudentHomepageHeader';
import StudentHomepageSidebarDesktop from './StudentHomepageSidebarDesktop';
import StudentHomepageSidebarMobile from './StudentHomepageSidebarMobile';
import StudentHomepageTabs from './StudentHomepageTabs';
import usePrevious from 'lib/hooks/usePrevious';
import useStudent from './hooks/useStudent';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

interface Props extends PropsWithChildrenRequired, WithRouterProps {}

export default function StudentHomepage({children, location, params}: Props) {
  const isStudentLoading = useStudent();

  const {classroomId} = params;

  const lastIsMobile = usePrevious(appStore.isMobileView());
  const isMobile = appStore.isMobileView();

  const {
    exceededHardCapLimit,
    setIsSidebarExpanded,
    setShowJoinClassModal,
    showJoinClassModal,
    studentHasInvalidEmail
  } = useStudentDashboard(
    useShallow((state) => ({
      exceededHardCapLimit: state.exceededHardCapLimit,
      setIsSidebarExpanded: state.setIsSidebarExpanded,
      setShowJoinClassModal: state.setShowJoinClassModal,
      showJoinClassModal: state.showJoinClassModal,
      studentHasInvalidEmail: state.studentHasInvalidEmail
    }))
  );

  // Force sidebar to expand/contract when viewport changes to/from mobile
  useEffect(() => {
    if (lastIsMobile !== isMobile) {
      setIsSidebarExpanded(!isMobile);
    }
  }, [isMobile, lastIsMobile, setIsSidebarExpanded]);

  return (
    <FeatureFlag name='new_student_dashboard'>
      <div
        className={classNames('-mt-[60px] min-h-[calc(100vh-60px)] background-subtle', {
          'flex flex-col': isMobile,
          'justify-center': isMobile && isStudentLoading,
          'grid grid-cols-[max-content_repeat(3,minmax(0,1fr))] grid-rows-[repeat(3,min-content)_1fr]':
            !isMobile
        })}
      >
        {isStudentLoading ? (
          <LoadingSpinner
            className={classNames({
              'col-span-4 row-span-4 self-center': !isMobile
            })}
            bottomMarginLevel={0}
            size={5}
          />
        ) : (
          <>
            {!isMobile && <StudentHomepageSidebarDesktop pathname={location.pathname} />}
            <StudentHomepageHeader className={classNames({'col-span-full': !isMobile})} />
            <div className='col-span-3'>
              <ClassroomInvitations
                className={classNames('mx-x2 mt-x4 mb-x0', {'col-span-3': !isMobile})}
              />
            </div>
            <StudentHomepageTabs
              className={classNames('mx-x2 mt-x3 rounded-t-3xl background-primary', {
                'flex flex-row overflow-x-auto': isMobile,
                'col-span-3': !isMobile
              })}
              classroomId={classroomId}
              pathname={location.pathname}
            />
            <div
              className={classNames('mx-x2 rounded-b-3xl background-primary', {
                'flex-1': isMobile,
                'col-span-3': !isMobile
              })}
              // Unmounts and re-mounts the tab content when the classroom scope changes.
              // This significantly simplifies code since the dashboard tabs care a lot about the classroom scope.
              // See: https://react.dev/learn/you-might-not-need-an-effect#resetting-all-state-when-a-prop-changes
              key={classroomId ?? 'home'}
            >
              {children}
            </div>
            {studentHasInvalidEmail && <InvalidEmailModal />}
            {exceededHardCapLimit && <HardCapStudentErrorModal />}
            {showJoinClassModal && (
              <JoinClassroomModal
                handleClose={() => setShowJoinClassModal(false)}
                isNewStudentDashboard
                onEnrollSuccess={() => setShowJoinClassModal(false)}
              />
            )}
          </>
        )}
      </div>
      {isMobile && <StudentHomepageSidebarMobile pathname={location.pathname} />}
    </FeatureFlag>
  );
}
